import { isGlobalNavHidden } from '../isGlobalNavHidden/isGlobalNavHidden';
import { NewsRoutes } from '#lib/websites/news/constants/routes.js';
import type { ReadonlyURL } from '$app/state';

// Read by the News iOS app's feed router: a link carrying it is opened in the
// device browser instead of inside the app.
export const OPEN_EXTERNAL_PARAM = 'open_external';

// True when `href` points at a page of the news app itself - `/news` or below on
// the site's own host. CMS links can be relative or absolute, so the href is
// resolved against the current page: a bare `/news/...` and a full
// `https://heni.com/news/...` are the same page, while `https://other.site/news`
// is not ours.
export const isNewsAppLink = (href: string, url: ReadonlyURL) => {
	let target: URL;
	try {
		target = new URL(href, url.href);
	} catch {
		return false;
	}

	return (
		target.host === url.host &&
		(target.pathname === NewsRoutes.Home ||
			target.pathname.startsWith(`${NewsRoutes.Home}/`))
	);
};

// The reserved HENI profile's component buttons are CMS free text and can point
// anywhere on the site (Editions, an artist page, the shop). Those are not news
// pages, so the app must not open them in its article modal. The app's feed
// webview has no JS bridge and can only see URLs, so the hint travels as a
// query param rather than a data attribute. Only stamped for app-webview
// visitors; on the web the href is returned untouched.
//
// A button that links back into the news app (`/news/...`) is not external:
// it is left alone here, so the caller can keep it in-app with
// `preserveGlobalNav` instead.
export const markExternalForApp = (
	href: string | null | undefined,
	url: ReadonlyURL
): string | null | undefined => {
	if (!href || !isGlobalNavHidden(url) || isNewsAppLink(href, url)) {
		return href;
	}

	const [base, hash] = href.split('#');
	const marked = `${base}${base.includes('?') ? '&' : '?'}${OPEN_EXTERNAL_PARAM}=1`;

	return hash ? `${marked}#${hash}` : marked;
};
